"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./AdminDashboard.css";

export default function AdminDashboard() {
  const [theme, setTheme] = useState("dark");
  const navigate = useNavigate();

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
      .matches
      ? "dark"
      : "light";
    const initialTheme = savedTheme || systemTheme;
    setTheme(initialTheme);
    document.documentElement.setAttribute("data-theme", initialTheme);
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/users`);
        setUsers(response.data);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    loadUsers();
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const [users, setUsers] = useState([]);

  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    admin: 0,
    password: "",
  });

  const [isAddingUser, setIsAddingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: "",
    email: "",
    admin: 0,
    password: "",
  });

  const handleStartAddUser = () => {
    setEditingUser(null);
    setNewUserForm({ name: "", email: "", admin: 0, password: "" });
    setIsAddingUser(true);
  };

  const handleCancelNewUser = () => {
    setIsAddingUser(false);
    setNewUserForm({ name: "", email: "", admin: 0, password: "" });
  };

  const handleSaveNewUser = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/users/create`,
        newUserForm
      );
      setUsers([...users, response.data]);
    } catch (error) {
      console.error("Error creating user:", error);
    }
    setIsAddingUser(false);
    setNewUserForm({ name: "", email: "", admin: 0, password: "" });
  };

  const handleNewUserInputChange = (field, value) => {
    setNewUserForm({ ...newUserForm, [field]: value });
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      name: user.name,
      email: user.email,
      admin: user.admin,
      password: user.password,
    });
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/auth/users/${editingUser}/edit`,
        editForm
      );
    } catch (error) {
      console.error("Error updating user:", error);
    }
    setUsers(
      users.map((user) =>
        user.id === editingUser ? { ...user, ...editForm } : user
      )
    );
    setEditingUser(null);
    setEditForm({ name: "", email: "", admin: 0, password: "" });
  };

  const handleCancel = () => {
    setEditingUser(null);
    setEditForm({ name: "", email: "", admin: 0, password: "" });
  };

  const handleDelete = async (user) => {
    if (
      window.confirm(`Are you sure you want to delete user "${user.name}"?`)
    ) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/auth/users/${user.id}/delete`
        );
      } catch (error) {
        console.error("Error deleting user:", error);
      }
      setUsers(users.filter((u) => u.id !== user.id));
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm({ ...editForm, [field]: value });
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="header-content">
          <div className="header-text">
            <h1>User Management</h1>
            <p>Manage user accounts, roles, and permissions</p>
          </div>
          <div className="header-controls">
            <button
              className="chat-button"
              onClick={() => navigate("/documents")}
              title="Go to Documents"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              Go to Documents
            </button>
            <button
              className="chat-button"
              onClick={() => navigate("/chat")}
              title="Go to Chat"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Go to Chat
            </button>
            <button
              className="theme-toggle"
              onClick={toggleTheme}
              title="Toggle theme"
            >
              {theme === "dark" ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="table-toolbar">
          <button
            className="add-user-button"
            onClick={handleStartAddUser}
            title="Add New User"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            New User
          </button>
        </div>
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                {(editingUser || isAddingUser) && <th>New Password</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isAddingUser && (
                <tr className="editing new-user-row">
                  <td>
                    <input
                      type="text"
                      className="edit-input"
                      value={newUserForm.name}
                      onChange={(e) =>
                        handleNewUserInputChange("name", e.target.value)
                      }
                      placeholder="Full name"
                      autoFocus
                    />
                  </td>
                  <td>
                    <input
                      type="email"
                      className="edit-input"
                      value={newUserForm.email}
                      onChange={(e) =>
                        handleNewUserInputChange("email", e.target.value)
                      }
                      placeholder="Email address"
                    />
                  </td>
                  <td>
                    <select
                      className="edit-select"
                      value={newUserForm.admin}
                      onChange={(e) =>
                        handleNewUserInputChange("admin", e.target.value)
                      }
                    >
                      <option value="1">Admin</option>
                      <option value="0">User</option>
                    </select>
                  </td>
                  <td>
                    <input
                      type="password"
                      className="edit-input"
                      value={newUserForm.password}
                      onChange={(e) =>
                        handleNewUserInputChange("password", e.target.value)
                      }
                      placeholder="Password"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="save-button"
                        onClick={handleSaveNewUser}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Save
                      </button>
                      <button
                        className="cancel-button"
                        onClick={handleCancelNewUser}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {users.length > 0 ? (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className={editingUser === user.id ? "editing" : ""}
                  >
                    {editingUser === user.id ? (
                      <>
                        <td>
                          <input
                            type="text"
                            className="edit-input"
                            value={editForm.name}
                            onChange={(e) =>
                              handleInputChange("name", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="email"
                            className="edit-input"
                            value={editForm.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                          />
                        </td>
                        <td>
                          <select
                            className="edit-select"
                            value={editForm.admin}
                            onChange={(e) =>
                              handleInputChange("admin", e.target.value)
                            }
                          >
                            <option value="1">Admin</option>
                            <option value="0">User</option>
                          </select>
                        </td>
                        <td>
                          <input
                            type="password"
                            className="edit-input"
                            value={editForm.password || "****************"}
                            onChange={(e) =>
                              handleInputChange("password", e.target.value)
                            }
                            onFocus={(e) => {
                              e.target.value = "";
                            }}
                            placeholder="Enter new password"
                          />
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="save-button"
                              onClick={handleSave}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              Save
                            </button>
                            <button
                              className="cancel-button"
                              onClick={handleCancel}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          <div className="user-name-cell">
                            <div className="user-avatar-small">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {user.name}
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>
                          <span
                            className={`role-badge ${
                              user.admin.toString() === "1" ? "admin" : "user"
                            }`}
                          >
                            {user.admin.toString() === "1" ? "Admin" : "User"}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="edit-button"
                              onClick={() => handleEdit(user)}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit
                            </button>
                            <button
                              className="delete-button"
                              onClick={() => handleDelete(user)}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                !isAddingUser && (
                  <tr>
                    <td colSpan="5" className="no-users">
                      No users found.
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
